import { daysBetween } from "../../lib/funnel/helpers";
import type { BaselineRow } from "./sql";

// True when the user has any non-null milestone date and it falls within
// `withinDays` of their sign-up. Used by every "% within N days" KPI.
function within(signupIso: string, milestoneIso: string | null, withinDays: number): boolean {
  if (!milestoneIso) return false;
  const d = daysBetween(signupIso, milestoneIso);
  return d >= 0 && d <= withinDays;
}

export const firstSummaryWithin24h = (u: BaselineRow): boolean =>
  within(u.signup_date_utc, u.first_synopsis_date, 1);

export const twoSummariesWithin14d = (u: BaselineRow): boolean =>
  u.synopsis_count >= 2 && within(u.signup_date_utc, u.second_synopsis_date, 14);

export const customTemplateWithin14d = (u: BaselineRow): boolean =>
  within(u.signup_date_utc, u.first_custom_template_date, 14);

export const calendarConnected = (u: BaselineRow): boolean =>
  u.first_calendar_date != null;

export const calendarWithin7d = (u: BaselineRow): boolean =>
  within(u.signup_date_utc, u.first_calendar_date, 7);

export const plusWithin30d = (u: BaselineRow): boolean =>
  within(u.signup_date_utc, u.first_plus_date, 30);

// Median across the cohort's non-null days-to-first-summary. Users who never
// summarised are excluded from both numerator and denominator — the KPI
// answers "how long does it take, given they do it".
export function medianDaysToFirstSummary(rows: BaselineRow[]): number | null {
  const vals: number[] = [];
  for (const r of rows) {
    if (r.days_to_first_synopsis != null && Number.isFinite(r.days_to_first_synopsis)) {
      vals.push(r.days_to_first_synopsis);
    }
  }
  if (vals.length === 0) return null;
  vals.sort((a, b) => a - b);
  const mid = Math.floor(vals.length / 2);
  const a = vals[mid];
  if (vals.length % 2 === 0) {
    const b = vals[mid - 1];
    if (a == null || b == null) return null;
    return (a + b) / 2;
  }
  return a ?? null;
}

// Wilson 95% confidence interval half-width in percentage points. Used on the
// KPI tiles so an A/B read-out has a noise floor — any delta smaller than the
// sum of the two cohorts' half-widths is inside the band and shouldn't be
// claimed as a real effect.
export function wilsonHalfPp(k: number, n: number): number {
  if (n <= 0) return 0;
  const z = 1.959963984540054;
  const p = k / n;
  const denom = 1 + (z * z) / n;
  const half = (z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / denom;
  return half * 100;
}

// Approximate minimum detectable lift (in pp) at two-sided 95% / 80% power,
// assuming an equal-sized comparison cohort and baseline ~p (use 0.5 as the
// conservative worst case when p isn't known a priori). For tile copy only —
// the real number depends on the metric's baseline rate.
export function mdeAtBaseline(n: number, pBaseline = 0.5): number {
  if (n <= 0) return 0;
  const zAlpha = 1.959963984540054;
  const zBeta = 0.8416212335729143;
  const se = Math.sqrt((2 * pBaseline * (1 - pBaseline)) / n);
  return (zAlpha + zBeta) * se * 100;
}

// Deduplicate per user; a single user can appear once per active company role.
// For the cohort KPIs we want one row per user, keeping the row with the most
// activity (so we don't drop the workspace where they actually used Marloo).
export function dedupeByUser(rows: BaselineRow[]): BaselineRow[] {
  const score = (u: BaselineRow): number =>
    u.synopsis_count * 100 +
    u.transcript_count * 10 +
    u.custom_template_count * 5 +
    (u.on_paid_plan ? 1000 : 0);
  const best = new Map<string, BaselineRow>();
  for (const r of rows) {
    const cur = best.get(r.user_external_id);
    if (!cur || score(r) > score(cur)) best.set(r.user_external_id, r);
  }
  return Array.from(best.values());
}

export type FunnelStep = {
  key: string;
  label: string;
  predicate: (u: BaselineRow) => boolean;
};

// Activation funnel for the baseline cohort. Each step is cumulative on top
// of "signed up" — % of cohort that reached at least this milestone.
export const FUNNEL_STEPS: FunnelStep[] = [
  { key: "signup",           label: "Signed up",            predicate: () => true },
  { key: "calendar",         label: "Connected calendar",   predicate: (u) => u.first_calendar_date != null },
  { key: "transcript",       label: "First transcript",     predicate: (u) => u.transcript_count > 0 },
  { key: "first_synopsis",   label: "First summary",        predicate: (u) => u.synopsis_count > 0 },
  { key: "second_synopsis",  label: "2 summaries (14d)",    predicate: twoSummariesWithin14d },
  { key: "custom_template",  label: "Custom template (14d)", predicate: customTemplateWithin14d },
  { key: "paid",             label: "Converted to paid",    predicate: (u) => u.paid_converted_date_utc != null },
];

export const FEATURES: { key: string; label: string; touched: (u: BaselineRow) => boolean }[] = [
  { key: "calendar",          label: "Calendar connect",      touched: (u) => u.first_calendar_date != null },
  { key: "transcript",        label: "Meeting transcript",    touched: (u) => u.transcript_count > 0 },
  { key: "synopsis",          label: "Meeting summary",       touched: (u) => u.synopsis_count > 0 },
  { key: "custom_template",   label: "Custom template",       touched: (u) => u.custom_template_count > 0 },
  { key: "ask_marloo_meet",   label: "Ask Marloo (meeting)",  touched: (u) => u.chat_meeting_count > 0 },
  { key: "ask_marloo_client", label: "Ask Marloo (client)",   touched: (u) => u.chat_client_count > 0 },
  { key: "document",          label: "Document generated",    touched: (u) => u.document_count > 0 },
];

// A feature row in the weekly heatmap. `eventDate(u)` returns the iso date of
// the user's nth event (e.g. first synopsis, fifth synopsis); the row shows
// the cumulative % of cohort whose nth event landed within each week bucket.
// `children` is an optional set of deeper thresholds that the row expands to.
export type HeatmapFeature = {
  key: string;
  label: string;
  eventDate: (u: BaselineRow) => string | null;
  children?: HeatmapFeature[];
};

export const HEATMAP_FEATURES: HeatmapFeature[] = [
  {
    key: "calendar",
    label: "Connected calendar",
    eventDate: (u) => u.first_calendar_date,
  },
  {
    key: "transcript",
    label: "Generated a transcript",
    eventDate: (u) => u.first_transcript_date,
    children: [
      { key: "transcript_2", label: "2+ transcripts", eventDate: (u) => u.second_transcript_date },
      { key: "transcript_5", label: "5+ transcripts", eventDate: (u) => u.fifth_transcript_date },
    ],
  },
  {
    key: "synopsis",
    label: "Generated a summary",
    eventDate: (u) => u.first_synopsis_date,
    children: [
      { key: "synopsis_2", label: "2+ summaries", eventDate: (u) => u.second_synopsis_date },
      {
        key: "synopsis_2_in_14d",
        label: "2+ summaries in first 14 days",
        eventDate: (u: BaselineRow) => {
          if (!u.second_synopsis_date) return null;
          return daysBetween(u.signup_date_utc, u.second_synopsis_date) <= 14
            ? u.second_synopsis_date
            : null;
        },
      },
      { key: "synopsis_5", label: "5+ summaries", eventDate: (u) => u.fifth_synopsis_date },
    ],
  },
  {
    key: "custom_template",
    label: "Created a custom template",
    eventDate: (u) => u.first_custom_template_date,
    children: [
      { key: "custom_template_2", label: "2+ custom templates", eventDate: (u) => u.second_custom_template_date },
      { key: "custom_template_5", label: "5+ custom templates", eventDate: (u) => u.fifth_custom_template_date },
    ],
  },
  {
    key: "ask_marloo_meet",
    label: "Asked Marloo (meeting)",
    eventDate: (u) => u.first_chat_meeting_date,
    children: [
      { key: "ask_marloo_meet_2", label: "2+ meeting chats", eventDate: (u) => u.second_chat_meeting_date },
      { key: "ask_marloo_meet_5", label: "5+ meeting chats", eventDate: (u) => u.fifth_chat_meeting_date },
    ],
  },
  {
    key: "ask_marloo_client",
    label: "Asked Marloo (client)",
    eventDate: (u) => u.first_chat_client_date,
    children: [
      { key: "ask_marloo_client_2", label: "2+ client chats", eventDate: (u) => u.second_chat_client_date },
      { key: "ask_marloo_client_5", label: "5+ client chats", eventDate: (u) => u.fifth_chat_client_date },
    ],
  },
  {
    key: "document",
    label: "Generated a document",
    eventDate: (u) => u.first_document_date,
    children: [
      { key: "document_2", label: "2+ documents", eventDate: (u) => u.second_document_date },
      { key: "document_5", label: "5+ documents", eventDate: (u) => u.fifth_document_date },
    ],
  },
  {
    key: "task",
    label: "Created a task",
    eventDate: (u) => u.first_task_date,
    children: [
      { key: "task_2", label: "2+ tasks", eventDate: (u) => u.second_task_date },
      { key: "task_5", label: "5+ tasks", eventDate: (u) => u.fifth_task_date },
      { key: "task_updated", label: "Updated a task", eventDate: (u) => u.first_task_update_date },
    ],
  },
  {
    key: "client",
    label: "Created a client",
    eventDate: (u) => u.first_client_date,
    children: [
      { key: "client_2", label: "2+ clients", eventDate: (u) => u.second_client_date },
      { key: "client_5", label: "5+ clients", eventDate: (u) => u.fifth_client_date },
    ],
  },
  {
    key: "form",
    label: "Filled a form",
    eventDate: (u) => u.first_form_date,
    children: [
      { key: "form_2", label: "2+ forms", eventDate: (u) => u.second_form_date },
      { key: "form_5", label: "5+ forms", eventDate: (u) => u.fifth_form_date },
    ],
  },
];

// Conversion-table feature list: same shape as the heatmap, plus a synthesised
// "Invited a team member" parent row (the heatmap omits it because team
// invites don't fit the weekly reach framing).
export const CONV_FEATURES: HeatmapFeature[] = [
  ...HEATMAP_FEATURES,
  {
    key: "team_invite",
    label: "Invited a team member",
    eventDate: (u) => u.first_teammate_join_date,
    children: [
      {
        key: "team_invite_1_in_14d",
        label: "1+ invited in first 14 days",
        eventDate: (u) => {
          if (!u.first_teammate_join_date) return null;
          return daysBetween(u.signup_date_utc, u.first_teammate_join_date) <= 14
            ? u.first_teammate_join_date
            : null;
        },
      },
      { key: "team_invite_2", label: "2+ team members", eventDate: (u) => u.second_teammate_join_date },
      { key: "team_invite_5", label: "5+ team members", eventDate: (u) => u.fifth_teammate_join_date },
    ],
  },
];

// Week buckets used as columns in the feature heatmap. Each value is the
// inclusive upper bound in days from signup. Cohort minimum observation is 30
// days, so columns past W4 use a shrunk denominator (only users old enough to
// have had that much time).
export const HEATMAP_WEEKS: { key: string; label: string; days: number }[] = [
  { key: "w1",  label: "Week 1",  days: 7 },
  { key: "w2",  label: "Week 2",  days: 14 },
  { key: "w3",  label: "Week 3",  days: 21 },
  { key: "w4",  label: "Week 4",  days: 28 },
  { key: "w8",  label: "Week 8",  days: 56 },
  { key: "w12", label: "Week 12", days: 84 },
];

// Histogram buckets for days-from-signup-to-first-summary. 0 = same day.
// Past 14d users have missed the 2-in-14 KPI, so we collapse into one tail bucket.
export const TTS_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: "0d",      min: 0,  max: 1 },
  { label: "1d",      min: 1,  max: 2 },
  { label: "2-3d",    min: 2,  max: 4 },
  { label: "4-7d",    min: 4,  max: 8 },
  { label: "8-14d",   min: 8,  max: 15 },
  { label: "15-30d",  min: 15, max: 31 },
  { label: "30d+",    min: 31, max: Infinity },
];

export function bucketTimeToSummary(rows: BaselineRow[]): { label: string; value: number }[] {
  const counts = TTS_BUCKETS.map(() => 0);
  for (const r of rows) {
    if (r.first_synopsis_date == null) continue;
    const d = daysBetween(r.signup_date_utc, r.first_synopsis_date);
    for (let i = 0; i < TTS_BUCKETS.length; i++) {
      const b = TTS_BUCKETS[i];
      if (b && d >= b.min && d < b.max) { counts[i] = (counts[i] ?? 0) + 1; break; }
    }
  }
  return TTS_BUCKETS.map((b, i) => ({ label: b.label, value: counts[i] ?? 0 }));
}
